import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { deleteBells, getBellList, setReadStatus } from '@/api/bells';
import { getCache, getCacheInterval, setCache } from '@/helper/cache';
import { BROADCAST_TYPE, piniaStoreSynchronizer } from '@/broadcastChannel';
const bellsRateLimitInterval = 60000; // 1 minute in milliseconds
const cacheRequestName = 'bells';
const pageSize = 20;
export const useBellsStore = defineStore('bells', () => {
    const bells = ref([]);
    const limit = ref(pageSize);
    const finishedFirstLoad = ref(false);
    // getters
    const unreadCount = computed(() => {
        if (!Array.isArray(bells.value)) {
            return { count: 0, maybeMore: false };
        }
        const count = bells.value.filter(b => !b.isRead).length;
        const maybeMore = !(count < bells.value.length || areAllLoaded.value);
        return { count, maybeMore };
    });
    const areAllLoaded = computed(() => finishedFirstLoad.value && bells.value.length < limit.value);
    // actions
    async function fetch(withoutCache = false) {
        try {
            if (await getCacheInterval(cacheRequestName, bellsRateLimitInterval) || withoutCache) {
                bells.value = await getBellList(limit.value);
                await setCache(cacheRequestName, bells.value);
                finishedFirstLoad.value = true;
            }
            else {
                bells.value = await getCache(cacheRequestName) || [];
                limit.value = Math.max(pageSize, Math.ceil(bells.value.length / pageSize) * pageSize);
                finishedFirstLoad.value = true;
            }
        }
        catch (e) {
            console.error('Error fetching bells:', e);
        }
    }
    async function deleteAction(ids) {
        try {
            await deleteBells(ids);
            bells.value = bells.value.filter(b => !ids.includes(b.id));
            await setCache(cacheRequestName, bells.value);
            await fetch(true);
        }
        catch (err) {
            console.log(err);
            throw err;
        }
    }
    async function markAsRead(bell) {
        const bellsToMarkAsRead = allBellsWithSameHref(bell);
        await markBells(bellsToMarkAsRead);
    }
    async function setReadStatusAction(bell, isRead) {
        const bellsToMark = allBellsWithSameHref(bell);
        await markBells(bellsToMark, isRead);
    }
    async function markNewBellsAsRead() {
        const bellsToMarkAsRead = bells.value.filter(bell => !bell.isRead);
        await markBells(bellsToMarkAsRead);
    }
    function allBellsWithSameHref(bell) {
        return bells.value.filter(b => b.href === bell.href);
    }
    async function markBells(bellsToMark, isRead = true) {
        if (bellsToMark.length === 0) {
            return;
        }
        const ids = bellsToMark.map(bell => bell.id);
        try {
            await setReadStatus(ids, isRead);
            bellsToMark.forEach(bellToMark => {
                bellToMark.isRead = isRead;
            });
            await setCache(cacheRequestName, bells.value);
        }
        catch (err) {
            console.error('Error marking bells:', err);
        }
    }
    async function loadMore() {
        if (bells.value.length === limit.value) {
            limit.value += pageSize;
            finishedFirstLoad.value = false;
        }
        await fetch(true);
    }
    piniaStoreSynchronizer(BROADCAST_TYPE.UPDATE_BELLS, 'bells', bells);
    return {
        bells,
        limit,
        finishedFirstLoad,
        unreadCount,
        areAllLoaded,
        fetch,
        delete: deleteAction,
        markAsRead,
        setReadStatus: setReadStatusAction,
        markNewBellsAsRead,
        markBells,
        allBellsWithSameHref,
        loadMore,
    };
});
